/**
 * Presentational chrome for the Shogun panel.
 *
 * Pure props/state — no SDK, no chat, no bridge. It renders the dockable frame
 * and hosts arbitrary `children` (the live Shogun chat in production; a stub in
 * functional stories). Two rendering ARMS:
 *
 *   - DOCKED (`left`/`right`/`top`/`bottom`): an IN-FLOW `relative` lane inside
 *     the shell flex container, clamped to ≤ 50% of its axis (`max-w-[50%]` /
 *     `max-h-[50%]`), resizable by an edge handle on its content-facing side
 *     (`shogun-panel-resize-docked`), committed to localStorage on pointer-up.
 *     Content reflows beside it and the TitleBar stays
 *     structurally above — no overlay, no title-bar takeover.
 *   - FLOATING: a `fixed` overlay positioned by the clamped rect, draggable by
 *     the header and resizable by a bottom-right handle
 *     (`shogun-panel-resize`), min 320×240, committed to localStorage on
 *     pointer-up.
 *
 * Placement is the route's `handle.shogunDock` (app-shell ADR-016). Bottom/top
 * docks omit the frame header — the hosted chat already has its own title.
 * Floating and left/right keep the header (drag / side split).
 *
 * `data-testid="shogun-panel"` + `data-dock-mode` are stable across both arms
 * (test-contract continuity); combined with a stable tree position in
 * MainLayout, the hosted child keeps DOM-node identity across mode switches so
 * the live chat never remounts.
 */

import {
  useCallback,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  clampRect,
  type ShogunDockMode,
  type ShogunDockedSize,
  type ShogunFloatingRect,
} from './useShogunDock';

export interface ShogunPanelFrameProps {
  mode: ShogunDockMode;
  floating: ShogunFloatingRect;
  /** Update the floating rect LOCALLY (continuous during drag/resize; clamped). */
  onFloatingChange: (rect: ShogunFloatingRect) => void;
  /** Persist the current floating rect (called once on drag/resize END). */
  onFloatingCommit: () => void;
  /** Docked-lane size (width for left/right, height for top/bottom). */
  docked: ShogunDockedSize;
  /** Update the docked size LOCALLY (continuous during resize; clamped). */
  onDockedChange: (size: ShogunDockedSize) => void;
  /** Persist the current docked size (called once on resize END). */
  onDockedCommit: () => void;
  /** Omitted on route-forced docks (app-shell ADR-016). */
  onClose?: () => void;
  /** The agent this panel is hosting; the chat below carries its own header. */
  title?: string;
  children: ReactNode;
}

function viewport(): { vw: number; vh: number } {
  if (typeof window === 'undefined') return { vw: 1280, vh: 800 };
  return { vw: window.innerWidth, vh: window.innerHeight };
}

/**
 * Per-arm container class + style. DOCKED is in-flow and axis-capped to ≤ 50%
 * of the shell container; FLOATING is a fixed overlay at the
 * clamped rect. `borderClass` gives a docked panel a single separating edge and
 * a floating panel a full rounded/shadowed card.
 */
function armClassAndStyle(
  mode: ShogunDockMode,
  floating: ShogunFloatingRect,
  docked: ShogunDockedSize,
): { className: string; style: CSSProperties } {
  const borderClass =
    mode === 'left'
      ? 'border-r'
      : mode === 'right'
        ? 'border-l'
        : mode === 'top'
          ? 'border-b'
          : mode === 'bottom'
            ? 'border-t'
            : 'border rounded-lg shadow-2xl';

  if (mode === 'floating') {
    const { vw, vh } = viewport();
    const r = clampRect(floating, vw, vh);
    // Map the geometry rect (x/y) onto CSS positioning (left/top) — a `fixed`
    // element ignores `x`/`y`, so returning the raw rect would leave the panel
    // unpositioned (stuck at its auto origin, immovable).
    return {
      className: `fixed z-40 ${borderClass}`,
      style: { left: r.x, top: r.y, width: r.width, height: r.height },
    };
  }

  const horizontal = mode === 'left' || mode === 'right';
  return {
    className: `relative flex-none ${horizontal ? 'max-w-[50%]' : 'max-h-[50%]'} ${borderClass}`,
    // User-resizable docked lane size; `max-[50%]` above caps it on the
    // occupied axis so a stale persisted value can never dominate the shell.
    style: horizontal ? { width: docked.width } : { height: docked.height },
  };
}

export function ShogunPanelFrame({
  mode,
  floating,
  onFloatingChange,
  onFloatingCommit,
  docked,
  onDockedChange,
  onDockedCommit,
  onClose,
  title = 'Shogun',
  children,
}: ShogunPanelFrameProps) {

  // Drag/resize use WINDOW-level pointermove/up listeners attached on
  // pointerdown (removed on pointerup) rather than element `setPointerCapture`.
  // Window listeners catch every move regardless of what's under the cursor, so
  // the gesture is reliable for a real mouse AND for synthetic/injected pointer
  // streams (Playwright) — `setPointerCapture` silently drops the latter. The
  // grab geometry is captured once at pointerdown; width/height are held during
  // a drag and x/y during a resize. `onFloatingChange` clamps every proposal.
  const onHeaderPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (mode !== 'floating') return;
      // Ignore drags that start on an interactive control in the header.
      if ((e.target as HTMLElement).closest('button')) return;
      const dx = e.clientX - floating.x;
      const dy = e.clientY - floating.y;
      const width = floating.width;
      const height = floating.height;
      const onMove = (ev: PointerEvent) => {
        onFloatingChange({ x: Math.max(0, ev.clientX - dx), y: Math.max(0, ev.clientY - dy), width, height });
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        // Persist the dragged position so it is restored after reload.
        onFloatingCommit();
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [mode, floating.x, floating.y, floating.width, floating.height, onFloatingChange, onFloatingCommit],
  );

  const onResizePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.stopPropagation(); // never start a header drag from the resize grip
      const px = e.clientX;
      const py = e.clientY;
      const x = floating.x;
      const y = floating.y;
      const w = floating.width;
      const h = floating.height;
      const onMove = (ev: PointerEvent) => {
        // onFloatingChange clamps (≥ 320×240, within viewport) — the handle only
        // proposes the raw extension.
        onFloatingChange({ x, y, width: w + (ev.clientX - px), height: h + (ev.clientY - py) });
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        onFloatingCommit();
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [floating.x, floating.y, floating.width, floating.height, onFloatingChange, onFloatingCommit],
  );

  // Docked resize: the handle sits on the CONTENT-FACING edge (the same edge
  // that carries the border class), so for right/bottom docks the panel grows
  // as the pointer moves toward the content — a negative delta.
  const onDockedResizePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const horizontal = mode === 'left' || mode === 'right';
      const start = horizontal ? e.clientX : e.clientY;
      const startSize = horizontal ? docked.width : docked.height;
      const sign = mode === 'left' || mode === 'top' ? 1 : -1;
      const onMove = (ev: PointerEvent) => {
        // onDockedChange clamps — the handle only proposes the raw extension.
        const next = startSize + sign * ((horizontal ? ev.clientX : ev.clientY) - start);
        onDockedChange(horizontal ? { ...docked, width: next } : { ...docked, height: next });
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        onDockedCommit();
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [mode, docked, onDockedChange, onDockedCommit],
  );

  const dockedHandleClass =
    mode === 'left'
      ? 'inset-y-0 right-0 w-1 cursor-col-resize'
      : mode === 'right'
        ? 'inset-y-0 left-0 w-1 cursor-col-resize'
        : mode === 'top'
          ? 'inset-x-0 bottom-0 h-1 cursor-row-resize'
          : 'inset-x-0 top-0 h-1 cursor-row-resize';

  const arm = armClassAndStyle(mode, floating, docked);

  return (
    <div
      data-testid="shogun-panel"
      data-dock-mode={mode}
      className={`flex flex-col overflow-hidden border-border bg-background ${arm.className}`}
      style={arm.style}
    >
      {(mode === 'floating' || mode === 'left' || mode === 'right') && (
      <div
        data-testid="shogun-panel-header"
        onPointerDown={onHeaderPointerDown}
        // `select-none` + `touch-none`: dragging the panel by its header must
        // never start a text selection — a selection fires `pointercancel`,
        // which silently aborts the drag mid-gesture.
        className={`flex select-none items-center justify-between border-b border-border px-3 py-2 ${mode === 'floating' ? 'cursor-move touch-none' : ''}`}
      >
        <span className="text-label font-semibold">{title}</span>
        {/* Controls: stopPropagation so interacting with them never starts a
            floating drag (the header itself is the drag handle). */}
        <div className="relative flex items-center gap-1" onPointerDown={(e) => e.stopPropagation()}>
          {onClose && (
            <button
              data-testid="shogun-panel-close"
              title="Close Shogun"
              onClick={onClose}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden" data-testid="shogun-panel-body">
        {children}
      </div>
      {/* Bottom-right resize grip — floating only. */}
      {mode === 'floating' ? (
        <div
          data-testid="shogun-panel-resize"
          onPointerDown={onResizePointerDown}
          title="Resize Shogun"
          aria-label="Resize Shogun"
          className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize touch-none"
        />
      ) : (
        <div
          data-testid="shogun-panel-resize-docked"
          onPointerDown={onDockedResizePointerDown}
          title="Resize panel"
          aria-label="Resize panel"
          className={`absolute z-10 touch-none select-none hover:bg-primary/40 active:bg-primary/60 ${dockedHandleClass}`}
        />
      )}
    </div>
  );
}
